import {
  formatCents,
  orderLineShortlist,
  parseCreditMemo,
  pricesBelongOnOrder,
  receiptLineMatches,
  reconcileOrder,
  type Discrepancy,
  type Reconciliation,
} from "@roof-hawk/core";
import { useState } from "react";

import { Modal } from "../../components/ui";
import { t, tn } from "../../i18n";
import { insertRow, updateRow } from "../../lib/queries";
import { useSession } from "../../session";

/**
 * Checking a supplier's acknowledgement against what we ordered.
 *
 * Three things can come out of this and they are deliberately three separate
 * decisions, because they have different consequences:
 *
 *   - Query it with the supplier. A draft email, opened in the mail client for
 *     a person to read and send. Nothing is sent from here.
 *   - Accept a new price into the catalogue. This reprices every estimate
 *     generated from now on, so it asks twice and is written to the undo log.
 *   - Do nothing, which is often right: a one-off freight charge is not a
 *     reason to change what a bundle of shingles costs.
 */
export function ReconcileModal({
  order,
  supplierName,
  supplierEmail,
  documentName,
  documentKind,
  reconciliation,
  onClose,
  onChanged,
}: {
  order: { id: string; order_number: number; tax_rate?: unknown };
  supplierName: string | null;
  supplierEmail: string | null;
  documentName: string;
  /** The `job_files.kind` of the document checked: only an invoice's prices go on the order. */
  documentKind: string;
  reconciliation: Reconciliation;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { profileId } = useSession();
  const [applied, setApplied] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  /** The rate taken off the receipt, once somebody has taken it. */
  const [tookRate, setTookRate] = useState<number | null>(null);

  const { discrepancies, matched, netImpact, ourTotal, theirTotal, tax } =
    reconciliation;

  /**
   * Put the supplier's rate on this order.
   *
   * One order, not the catalogue: `material_orders.tax_rate` is where sales tax
   * lives (ADR 0016), so this changes what this order costs the job and nothing
   * else. That is why it asks once and not twice, unlike a price.
   */
  async function takeTheirRate(rate: number) {
    if (
      !window.confirm(
        t("Tax this order at {rate}%?", { rate }) +
          "\n\n" +
          t(
            "The order is at {ours}% and {document} charges {rate}%. This changes what this order costs the job.",
            { ours: tax?.ourRate ?? 0, document: documentName, rate },
          ),
      )
    )
      return;
    setBusy(true);
    try {
      // Their rate replaces a typed amount too, or pressing this would change
      // nothing the order costs (ADR 0243).
      await updateRow("material_orders", order.id, {
        tax_rate: rate,
        tax_amount: null,
      });
      setTookRate(rate);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  const priceRows = discrepancies.filter(
    (d) => d.kind === "price" && d.productId,
  );

  /**
   * The order lines the document prices differently, when it is an invoice,
   * a receipt or a credit memo (ADR 0311). An acknowledgement's are not.
   */
  const orderPriceRows = pricesBelongOnOrder(documentKind)
    ? discrepancies.filter(
        (d) => d.kind === "price" && d.itemId && d.theirPrice !== null,
      )
    : [];
  /** Once the document's prices are on the order. */
  const [pricesOnOrder, setPricesOnOrder] = useState(false);

  /**
   * Put the document's prices on this order's lines, all at once.
   *
   * Jonathon, 2026-10-26, asked whether the check should offer one button
   * for this: "yes a button sounds good" (ADR 0311). One order, not the
   * catalogue — like taking the tax rate, it changes what this order costs
   * the job and nothing else, so it asks once. The catalogue price is still
   * its own decision, one line at a time, below.
   */
  async function putPricesOnOrder() {
    if (orderPriceRows.length === 0) return;
    const lines = orderPriceRows
      .map(
        (d) =>
          `• ${d.description}: $${d.ourPrice?.toFixed(2)} → $${d.theirPrice?.toFixed(2)}`,
      )
      .join("\n");
    if (
      !window.confirm(
        t("Put the prices on {document} on this order?", {
          document: documentName,
        }) +
          `\n\n${lines}\n\n` +
          t(
            "This changes what this order costs the job. The catalogue is not touched.",
          ),
      )
    )
      return;
    setBusy(true);
    try {
      for (const row of orderPriceRows) {
        if (!row.itemId) continue;
        await updateRow("material_order_items", row.itemId, {
          unit_price: row.theirPrice,
        });
      }
      setPricesOnOrder(true);
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  /**
   * Take the supplier's price into the catalogue.
   *
   * Two confirmations, because the blast radius is not obvious from where the
   * button sits: this is not a correction to one order, it is what every future
   * estimate will be built from.
   */
  async function acceptPrice(row: Discrepancy) {
    if (!row.productId || row.theirPrice === null) return;

    const first = window.confirm(
      t('Update the catalogue cost of "{part}"?', { part: row.description }) +
        "\n\n" +
        t("Now:  ${price}", { price: row.ourPrice?.toFixed(2) ?? "" }) +
        "\n" +
        t("New:  ${price}", { price: row.theirPrice.toFixed(2) }) +
        "\n\n" +
        t("This does not just fix this order."),
    );
    if (!first) return;

    const second = window.confirm(
      t("Are you sure?") +
        "\n\n" +
        t(
          "Every estimate generated from now on will use the new price. Estimates already written are not touched.",
        ) +
        "\n\n" +
        // Tools → Price Changes was removed on 2026-09-07 at Jonathon's
        // request, so Undo on this row is the only way back. Say so, rather
        // than sending somebody to a screen that is not there.
        t(
          "Undo on this row puts it back. Once this window is closed, changing it again means editing the product in Tools.",
        ),
    );
    if (!second) return;

    setBusy(true);
    try {
      const change = await insertRow<{ id: string }>("price_changes", {
        product_id: row.productId,
        field: "default_cost",
        old_value: row.ourPrice,
        new_value: row.theirPrice,
        source: `Reconciled against ${documentName}`,
        material_order_id: order.id,
        changed_by: profileId,
      });
      await updateRow("products", row.productId, {
        default_cost: row.theirPrice,
      });
      setApplied((a) => ({ ...a, [row.productId!]: change.id }));
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  /** Put a price back. A new log row, not a deletion — see the migration. */
  async function undoPrice(row: Discrepancy) {
    if (!row.productId || row.ourPrice === null) return;
    const changeId = applied[row.productId];
    setBusy(true);
    try {
      await insertRow("price_changes", {
        product_id: row.productId,
        field: "default_cost",
        old_value: row.theirPrice,
        new_value: row.ourPrice,
        source: "Undo",
        material_order_id: order.id,
        undoes_id: changeId ?? null,
        changed_by: profileId,
      });
      await updateRow("products", row.productId, {
        default_cost: row.ourPrice,
      });
      setApplied((a) => {
        const next = { ...a };
        delete next[row.productId!];
        return next;
      });
      await onChanged();
    } finally {
      setBusy(false);
    }
  }

  /**
   * A draft email, opened in the mail client.
   *
   * Never sent from here. What to say to a supplier about a short delivery is a
   * relationship, not a mail merge, and a person should read it first.
   */
  function draftEmail() {
    const lines = discrepancies.map((d) => {
      switch (d.kind) {
        case "qty":
          return `• ${d.description}: we ordered ${d.ourQty}, your paperwork shows ${d.theirQty}.`;
        case "price":
          return `• ${d.description}: we have $${d.ourPrice?.toFixed(2)}, your paperwork shows $${d.theirPrice?.toFixed(2)}.`;
        case "missing_from_supplier":
          return `• ${d.description}: ordered ${d.ourQty}, but we cannot find it on your paperwork.`;
        case "not_ordered":
          return `• ${d.description}: shows on your paperwork at $${d.theirPrice?.toFixed(2)} but we did not order it.`;
      }
    });

    // The tax is a line of the query like any other, and the one a supplier is
    // most often simply right about — which is why it is worth asking.
    if (tax && !tax.agrees) {
      lines.push(
        `• Sales tax: we have ${tax.ourRate}% (${formatCents(tax.ourTax)}), ` +
          `your paperwork shows ${formatCents(tax.theirTax)}` +
          `${tax.theirRate === null ? "" : ` (${tax.theirRate}%)`}.`,
      );
    }

    const body = [
      `Hello${supplierName ? ` ${supplierName}` : ""},`,
      "",
      `We have checked ${documentName} against our order MO-${order.order_number} and found the following:`,
      "",
      ...lines,
      "",
      `Our order totals ${formatCents(ourTotal)}; your paperwork totals ${formatCents(theirTotal)}.`,
      "",
      "Could you take a look and confirm which is right before this goes out?",
      "",
      "Thank you,",
    ].join("\n");

    window.open(
      `mailto:${supplierEmail ?? ""}?subject=${encodeURIComponent(
        `Query on order MO-${order.order_number}`,
      )}&body=${encodeURIComponent(body)}`,
      "_blank",
      "noopener",
    );
  }

  const label: Record<Discrepancy["kind"], string> = {
    qty: t("Quantity"),
    price: t("Price"),
    missing_from_supplier: t("Not on their paperwork"),
    not_ordered: t("We did not order this"),
  };

  return (
    <Modal
      title={t("Reconciliation — MO-{n}", { n: order.order_number })}
      subtitle={`${t("{document} checked against our order.", { document: documentName })} ${tn(
        matched,
        "1 line matched.",
        "{n} lines matched.",
      )}`}
      onClose={onClose}
      actions={
        <>
          {orderPriceRows.length > 0 && !pricesOnOrder && (
            <button
              className="btn-ghost"
              disabled={busy}
              onClick={() => void putPricesOnOrder()}
            >
              {t("Put their prices on the order")}
            </button>
          )}
          {pricesOnOrder && (
            <span className="sub">{t("order now at their prices")}</span>
          )}
          {(discrepancies.length > 0 || (tax !== null && !tax.agrees)) && (
            <button className="btn-ghost" onClick={draftEmail}>
              {t("Draft email to supplier")}
            </button>
          )}
          <button className="btn-primary" onClick={onClose}>
            {t("Close")}
          </button>
        </>
      }
    >
      <div className="totrow sub">
        <span className="lbl">{t("Our order")}</span>
        <span className="num">{formatCents(ourTotal)}</span>
        <span className="num" />
        <span className="num" />
      </div>
      <div className="totrow sub">
        <span className="lbl">{t("Their paperwork")}</span>
        <span className="num">{formatCents(theirTotal)}</span>
        <span className="num" />
        <span className="num" />
      </div>
      {/* Only when the document printed a tax line. A supplier who prints no
          tax has told us nothing, and a row of dashes reads like tax-exempt. */}
      {tax && (
        <div className="totrow sub">
          <span className="lbl">
            {t("Sales tax — ours {rate}%", { rate: String(tax.ourRate) })}
            {tax.theirRate === null
              ? ""
              : t(", theirs {rate}%", { rate: tax.theirRate })}
            {tax.theirRate !== null && !tax.agrees && tookRate === null && (
              <button
                className="btn-small"
                style={{ marginLeft: 8 }}
                disabled={busy}
                onClick={() => void takeTheirRate(tax.theirRate!)}
              >
                {t("Use {rate}%", { rate: tax.theirRate })}
              </button>
            )}
            {tookRate !== null && (
              <span className="sub" style={{ marginLeft: 8 }}>
                {t("order now taxed at {rate}%", { rate: tookRate })}
              </span>
            )}
          </span>
          <span className="num">{formatCents(tax.ourTax)}</span>
          <span className="num">{formatCents(tax.theirTax)}</span>
          <span className={`num ${tax.impact > 0 ? "overrun" : "credit"}`}>
            {tax.agrees ? "" : formatCents(tax.impact)}
          </span>
        </div>
      )}
      <div className="totrow strong">
        <span className="lbl">
          <b>{t("Difference")}</b>
        </span>
        <span className={`num ${netImpact > 0 ? "overrun" : "credit"}`}>
          <b>{formatCents(netImpact)}</b>
        </span>
        <span className="num" />
        <span className="num" />
      </div>

      {discrepancies.length === 0 ? (
        <div className={tax && !tax.agrees ? "warnbox" : "okbox"}>
          {t("Every line matches — quantities and prices agree line for line.")}
          {tax && !tax.agrees
            ? ` ${t(
                "The tax does not: this order is taxed at {rate}% and {document} charges {theirs}.",
                {
                  rate: String(tax.ourRate),
                  document: documentName,
                  theirs: formatCents(tax.theirTax),
                },
              )}`
            : ""}
        </div>
      ) : (
        <div className="ewrap" style={{ marginTop: 14 }}>
          <table className="etable">
            <thead>
              <tr>
                <th>{t("What")}</th>
                <th>{t("Item")}</th>
                <th className="num">{t("Ours")}</th>
                <th className="num">{t("Theirs")}</th>
                <th className="num">{t("Impact")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {discrepancies.map((d, i) => {
                const isApplied = d.productId
                  ? Boolean(applied[d.productId])
                  : false;
                return (
                  <tr key={`${d.kind}:${d.itemId ?? d.description}:${i}`}>
                    <td className="sub">{label[d.kind]}</td>
                    <td>{d.description}</td>
                    <td className="num">
                      {d.kind === "price"
                        ? `$${d.ourPrice?.toFixed(2)}`
                        : (d.ourQty ?? "—")}
                    </td>
                    <td className="num">
                      {d.kind === "price"
                        ? `$${d.theirPrice?.toFixed(2)}`
                        : (d.theirQty ?? "—")}
                    </td>
                    <td
                      className={`num ${d.costImpact > 0 ? "overrun" : "credit"}`}
                    >
                      {formatCents(d.costImpact)}
                    </td>
                    <td className="rowacts">
                      {d.kind === "price" && d.productId && (
                        <button
                          className="btn-small"
                          disabled={busy}
                          onClick={() =>
                            void (isApplied ? undoPrice(d) : acceptPrice(d))
                          }
                        >
                          {isApplied ? t("Undo") : t("Use their price")}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {priceRows.length > 0 && (
        <div className="na-hint" style={{ marginTop: 10 }}>
          {t(
            "Taking a price changes the catalogue for every estimate built from now on. Every change is logged and can be put back from Tools → Price Changes.",
          )}
        </div>
      )}
    </Modal>
  );
}

/** An order line, as both readers below want to see it. */
type OrderLine = {
  id: string;
  product_id: string | null;
  name: string | null;
  qty: unknown;
  unit_price: unknown;
};

/**
 * Read a supplier's document and compare it with the order.
 *
 * Two readers, in the order of what they cost. The text of a PDF is read in the
 * browser and is free, exact and instant, and most supplier paperwork arrives
 * as one. A photograph of a counter receipt has no text to read, so it goes to
 * the same language model that reads a supplier quotation on this screen — and
 * so does a PDF that turns out to be a scan, which looks identical to an empty
 * one from here.
 *
 * `taxRate` is the order's own rate, handed in so the comparison covers the
 * foot of the document as well as its lines: the receipt prints what was
 * actually charged in tax, and that is the one figure on a material order
 * nobody could check until now.
 */
export async function reconcileAgainstDocument(
  file: File,
  items: readonly OrderLine[],
  taxRate: unknown,
  /** The order, so lines word overlap cannot place can be put to Jev. */
  orderId?: string,
): Promise<Reconciliation | null> {
  const isPdf =
    file.type === "application/pdf" || /\.pdf$/i.test(file.name ?? "");

  if (isPdf) {
    const fromText = await readPdfText(file, items, taxRate, orderId);
    if (fromText) return fromText;
  }

  return await readWithModel(file, items, taxRate, orderId);
}

type SupplierRow = Parameters<typeof reconcileOrder>[1][number];
type Taxes = Parameters<typeof reconcileOrder>[3];

/**
 * Compare, and where word overlap left a receipt line and an order line both
 * unplaced, ask Jev whether they are the same and compare again (ADR 0155).
 *
 * Only the leftovers are asked about, and only among each other: a receipt
 * line can be matched to an order line nothing else claimed, never moved off
 * a match word overlap already made. If Jev cannot be reached, the first
 * comparison stands.
 */
async function reconcileWithJev(
  items: readonly OrderLine[],
  supplied: readonly SupplierRow[],
  taxes: Taxes,
  orderId: string | undefined,
): Promise<Reconciliation> {
  const first = reconcileOrder(items, supplied, undefined, taxes);
  if (!orderId) return first;

  // By position: two receipt lines can share a description, and only the one
  // word overlap left over is asked about.
  const unplacedTheirs = first.unplacedSupplied.map((i) => ({
    line: supplied[i]!,
    key: String(i),
  }));
  const unplacedIds = new Set(
    first.discrepancies
      .filter((d) => d.kind === "missing_from_supplier" && d.itemId)
      .map((d) => d.itemId!),
  );
  const unplacedOurs = items.filter((item) => unplacedIds.has(item.id));
  if (unplacedTheirs.length === 0 || unplacedOurs.length === 0) return first;

  try {
    const { matchSupplierLines } = await import("../../lib/supplierQuotes");
    const lines = unplacedTheirs.map(({ line, key }) => ({
      key,
      description: line.description,
    }));
    // A long order leaves more unplaced lines than one line may be offered,
    // so each receipt line gets the ones sharing the most words with it.
    const options = Object.fromEntries(
      lines.map((l) => [
        l.key,
        orderLineShortlist(l.description, unplacedOurs),
      ]),
    );
    const answers = await matchSupplierLines({ orderId, lines, options });
    const matches = receiptLineMatches(answers, lines);
    if (matches.size === 0) return first;
    return reconcileOrder(
      items,
      supplied,
      undefined,
      taxes,
      (index) => matches.get(String(index)) ?? null,
    );
  } catch {
    return first;
  }
}

/**
 * The cheap reader: the PDF's own text layer.
 *
 * An acknowledgement and a credit memo are the same shape of document — a
 * description, a quantity and two money columns — so the same parser handles
 * both, and is equally unwilling to guess. Null means it found nothing, which
 * is a scan rather than a failure.
 */
async function readPdfText(
  file: File,
  items: readonly OrderLine[],
  taxRate: unknown,
  orderId?: string,
): Promise<Reconciliation | null> {
  // Loaded on demand: pdf.js starts a web worker.
  const { extractPdfText } = await import("../../lib/roofPdf");
  const parsed = parseCreditMemo(await extractPdfText(file));
  if (parsed.lines.length === 0) return null;

  return reconcileWithJev(
    items,
    parsed.lines.map((l) => ({
      description: l.description,
      qty: l.qty,
      unitPrice: l.unitPrice,
      amount: l.amount,
    })),
    {
      ourRate: taxRate,
      theirs: {
        subtotal: parsed.subtotal,
        tax: parsed.tax,
        total: parsed.total,
      },
    },
    orderId,
  );
}

/**
 * The reader that can look at a picture.
 *
 * `read-supplier-quote` — the same function behind "Read A Supplier Quote", and
 * a receipt is the same document to it. It returns a price per unit rather than
 * a line total, so the extended amount is worked out here; that is arithmetic
 * on two numbers it read, not a figure invented for it.
 *
 * Null when it found no lines. A model that cannot see a line item is looking
 * at something that is not a receipt, and saying so is better than a
 * reconciliation against nothing.
 */
async function readWithModel(
  file: File,
  items: readonly OrderLine[],
  taxRate: unknown,
  orderId?: string,
): Promise<Reconciliation | null> {
  const { readSupplierQuote } = await import("../../lib/supplierQuotes");
  const quote = await readSupplierQuote(file);
  if (quote.lines.length === 0) return null;

  return reconcileWithJev(
    items,
    quote.lines.map((l) => ({
      description: l.description,
      qty: l.qty,
      unitPrice: l.unitPrice,
      amount: l.qty * l.unitPrice,
    })),
    {
      ourRate: taxRate,
      theirs: {
        subtotal: quote.printedSubtotal,
        tax: quote.printedTax,
        total: quote.printedTotal,
      },
    },
    orderId,
  );
}
